import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';

import Constants from '@helpers/Constants';
import { logError } from '@helpers/Loggers';
import {
  AppLinkPath,
  AuthRoute,
  CommonRoute,
  FollowUsersRoute,
  GroupRoute,
  HomeRoute,
  OnboardedStackRoute,
  OnboardedTabRoute,
  OnboardingRoute,
  UNIVERSAL_LINK_PREFIX,
} from '@navigation/Routes';
import type { RootNavigationParamList } from '@navigation/Types';
import { getPathFromState as getDefaultPathFromState } from '@react-navigation/native';
import type { LinkingOptions } from '@react-navigation/native';

const parseBooleanParam = (value: string) => value === 'true';
const commonRouteNames = new Set<string>(Object.values(CommonRoute));
const homePathRouteNames = new Set<string>([AuthRoute.Start, HomeRoute.Home]);

type LinkPathRoute = {
  name: string;
  params?: object;
  state?: LinkPathState;
};

type LinkPathState = {
  index?: number;
  routes: LinkPathRoute[];
};

const commonScreens = {
  [CommonRoute.CurrentUserProfile]: {
    exact: true,
    path: AppLinkPath.CurrentUserProfile,
  },
  [CommonRoute.UpdateUser]: {
    exact: true,
    path: AppLinkPath.EditCurrentUser,
  },
  [CommonRoute.Settings]: {
    exact: true,
    path: AppLinkPath.Settings,
  },
  [CommonRoute.CreateChat]: {
    exact: true,
    path: AppLinkPath.CreateChat,
  },
  [CommonRoute.Chat]: {
    exact: true,
    path: AppLinkPath.Chat,
  },
  [CommonRoute.ChatThreads]: {
    exact: true,
    path: AppLinkPath.Chats,
  },
  [CommonRoute.CreateGroup]: {
    exact: true,
    path: AppLinkPath.CreateGroup,
  },
  [CommonRoute.UpdateGroup]: {
    exact: true,
    path: AppLinkPath.EditGroup,
  },
  [CommonRoute.ViewGroupMembers]: {
    exact: true,
    path: AppLinkPath.GroupMembers,
  },
  [CommonRoute.ViewGroup]: {
    exact: true,
    path: AppLinkPath.Group,
    parse: {
      reload: parseBooleanParam,
    },
  },
  [CommonRoute.InviteContacts]: {
    exact: true,
    path: AppLinkPath.InviteContacts,
  },
  [CommonRoute.Notifications]: {
    exact: true,
    path: AppLinkPath.Notifications,
  },
  [CommonRoute.UpdatePlan]: {
    exact: true,
    path: AppLinkPath.EditPlan,
  },
  [CommonRoute.ViewPlan]: {
    exact: true,
    path: AppLinkPath.Plan,
    parse: {
      reload: parseBooleanParam,
    },
  },
  [CommonRoute.ViewFollows]: {
    exact: true,
    path: AppLinkPath.UserFollows,
  },
  [CommonRoute.Profile]: {
    exact: true,
    path: AppLinkPath.User,
  },
};

export const getLinking = (): LinkingOptions<RootNavigationParamList> => {
  const schemePrefixes = getSchemePrefixes();
  const config = {
    screens: {
      [AuthRoute.Login]: AppLinkPath.Login,
      [AuthRoute.Register]: AppLinkPath.Register,
      [AuthRoute.ResetPassword]: AppLinkPath.ResetPassword,
      [OnboardingRoute.CreateUser]: AppLinkPath.OnboardingCreateUser,
      [OnboardingRoute.UserPrivateForm]: AppLinkPath.OnboardingPrivateProfile,
      [OnboardingRoute.Welcome]: AppLinkPath.OnboardingWelcome,
      [OnboardingRoute.FollowUsers]: AppLinkPath.OnboardingFollowUsers,
      [OnboardingRoute.RequestPermissions]: AppLinkPath.OnboardingPermissions,
      [OnboardingRoute.JoinGroups]: AppLinkPath.OnboardingJoinGroups,
      [OnboardedStackRoute.CreatePlan]: AppLinkPath.CreatePlan,
      [OnboardedStackRoute.OnboardedTab]: {
        path: AppLinkPath.Home,
        screens: {
          [OnboardedTabRoute.Home]: {
            path: AppLinkPath.Home,
            screens: {
              [HomeRoute.Home]: AppLinkPath.Home,
              ...commonScreens,
            },
          },
          [OnboardedTabRoute.FollowUsers]: {
            path: AppLinkPath.People,
            screens: {
              [FollowUsersRoute.FollowUsers]: AppLinkPath.Home,
            },
          },
          [OnboardedTabRoute.Groups]: {
            path: AppLinkPath.Groups,
            screens: {
              [GroupRoute.ViewGroups]: AppLinkPath.Home,
            },
          },
        },
      },
    },
  };

  const linking = {
    prefixes: [...schemePrefixes, UNIVERSAL_LINK_PREFIX],
    config,
    getPathFromState: getCanonicalPathFromState,
  };
  return linking;
};

function getCanonicalPathFromState(
  state: Parameters<typeof getDefaultPathFromState>[0],
  options?: Parameters<typeof getDefaultPathFromState>[1]
) {
  const focusedRoute = getFocusedRoute(state as LinkPathState);

  if (!focusedRoute || !commonRouteNames.has(focusedRoute.name)) {
    if (focusedRoute && homePathRouteNames.has(focusedRoute.name)) {
      return '/';
    }

    return getDefaultPathFromState(state, options);
  }

  return getDefaultPathFromState(getCommonRoutePathState(focusedRoute), options);
}

function getFocusedRoute(state: LinkPathState) {
  const focusedIndex = state.index ?? state.routes.length - 1;
  const route = state.routes[focusedIndex];

  if (route?.state) {
    return getFocusedRoute(route.state);
  }

  return route;
}

function getCommonRoutePathState(route: LinkPathRoute) {
  return {
    routes: [
      {
        name: OnboardedStackRoute.OnboardedTab,
        state: {
          index: 0,
          routes: [
            {
              name: OnboardedTabRoute.Home,
              state: {
                index: 1,
                routes: [
                  { name: HomeRoute.Home },
                  {
                    name: route.name,
                    params: route.params,
                    state: route.state,
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}

function getSchemePrefixes() {
  const configuredSchemes = Array.isArray(Constants.scheme)
    ? Constants.scheme
    : Constants.scheme
      ? [Constants.scheme]
      : [];
  const expoPrefix = getExpoSchemePrefix(configuredSchemes[0]);
  const configuredPrefixes = configuredSchemes.map((scheme) => `${scheme}://`);
  return [...new Set([expoPrefix, ...configuredPrefixes].filter(isStringPrefix))];
}

function getExpoSchemePrefix(scheme?: string) {
  try {
    return Linking.createURL('/', { scheme });
  } catch {
    return undefined;
  }
}

function isStringPrefix(prefix: string | undefined): prefix is string {
  return typeof prefix === 'string';
}

export async function openSettings() {
  try {
    await Linking.openSettings();
  } catch (error) {
    logError(error);
  }
}

export async function openTermsPolicy() {
  try {
    await WebBrowser.openBrowserAsync(Constants.joinstar.company.links.terms);
  } catch (error) {
    logError(error);
  }
}

export async function openPrivacyPolicy() {
  try {
    await WebBrowser.openBrowserAsync(Constants.joinstar.company.links.privacy);
  } catch (error) {
    logError(error);
  }
}

export async function openCookiesPolicy() {
  try {
    await WebBrowser.openBrowserAsync(Constants.joinstar.company.links.cookies);
  } catch (error) {
    logError(error);
  }
}

export async function openContactUs() {
  try {
    await Linking.openURL(`mailto:${Constants.joinstar.company.emails.contact}`);
  } catch (error) {
    logError(error);
  }
}

export async function openDataPolicy() {
  try {
    await WebBrowser.openBrowserAsync(Constants.joinstar.company.links.data_request);
  } catch (error) {
    logError(error);
  }
}

export async function openReportPlan(planId: string) {
  try {
    await Linking.openURL(
      `mailto:${Constants.joinstar.company.emails.contact}?subject=Report%20Plan%20(Plan%20ID%3A%20${planId})&body=Please%20explain%20why%20you%20are%20reporting%20this%20plan%20below%3A`
    );
  } catch (error) {
    logError(error);
  }
}
